// @ts-strict-ignore
import {Component, computed, Injector, signal} from "@angular/core";
import {ActivationCondition, Assessable, AssessableItem, AssessableItemGroup, ChecklistItemType} from "@models";
import {SimUtilityService, TypePredicates} from "@services";
import {
	BasicMaterialDialog,
	MatDialogDataBaseInput,
	MatDialogDataBaseOutput,
} from "../../basic-material-dialog/basic-material-dialog.directive";

export interface DialogData_ChangeActivationConditionDialogInput<T extends AssessableItem> extends MatDialogDataBaseInput {
	assessable: Assessable<T>;
	group: AssessableItemGroup<T>;
}

export interface DialogData_ChangeActivationConditionDialogOutput extends MatDialogDataBaseOutput {
	activationCondition: ActivationCondition;
}

@Component({
	selector: "sim-change-activation-condition-dialog",
	templateUrl: "./change-activation-condition-dialog.component.html",
	styleUrls: ["./change-activation-condition-dialog.component.scss"],
	standalone: false,
})
export class ChangeActivationConditionDialogComponent<T extends AssessableItem> extends BasicMaterialDialog<
	ChangeActivationConditionDialogComponent<T>,
	DialogData_ChangeActivationConditionDialogInput<T>,
	DialogData_ChangeActivationConditionDialogOutput
> {
	protected readonly ChecklistItemType = ChecklistItemType;

	public assessmentItems: T[] = [];

	private _activatedBy = signal<T | null>(null);
	private _selectedValue = signal<unknown | null>(null);
	public activatedBy = this._activatedBy.asReadonly();
	public selectedValue = this._selectedValue.asReadonly();

	public maxStars = computed(() => this._computeMaxStars());
	public isSavingDisabled = computed(() => this._computeIsSavingDisabled());

	constructor(_injector: Injector) {
		super(_injector);
	}

	public ngOnInit() {
		super.ngOnInit();

		this.assessmentItems = this.data.assessable.assessableGroups
			.filter((g) => g.groupId !== this.data.group.groupId)
			.flatMap((g) => g.items)
			.concat(this.data.assessable.ungroupedItems)
			.filter((i) => i.type !== ChecklistItemType.FreeAnswerOption);

		if (this.data.group.activationCondition) {
			this._activatedBy.set(
				this.assessmentItems.find((item) => item.assessableItemId === this.data.group.activationCondition.activationItemId) ?? null,
			);
			this._selectedValue.set(this.data.group.activationCondition.activationItemAnswer);
		}
	}

	public updateActivatedBy(item: T) {
		if (!item) {
			this._selectedValue.set(null);
			this._activatedBy.set(null);
			return;
		}
		this._activatedBy.set(item);
		switch (item.type) {
			case ChecklistItemType.YesNoOption:
				this._selectedValue.set(false);
				break;
			case ChecklistItemType.ThreeStarOption:
				this._selectedValue.set(0);
				break;
			case ChecklistItemType.FreeAnswerOption:
				this._selectedValue.set("");
				break;
			case ChecklistItemType.SingleChoiceOption:
				this._selectedValue.set(null);
				break;
			case ChecklistItemType.MultipleChoiceOption:
				this._selectedValue.set([]);
				break;
		}
	}

	public updateAnswer(value: any) {
		this._selectedValue.set(value);
	}

	public updateMultipleChoiceAnswer([index, selected]: [number, boolean]) {
		const selectedValue = this.selectedValue();
		if (!Array.isArray(selectedValue)) {
			return;
		}

		this._selectedValue.set([...selectedValue]);
		if (selected) {
			this._selectedValue.set([...selectedValue, index]);
		} else {
			this._selectedValue.set(selectedValue.filter((i) => i !== index));
		}
	}

	public changeActivationCondition() {
		const result = this.activatedBy()
			? {
					activationItemId: this.activatedBy().assessableItemId,
					activationItemAnswer: this.selectedValue(),
				}
			: null;

		return this.close({action: "ok", activationCondition: result});
	}

	public stripHtml(html: string) {
		return SimUtilityService.stripHtml(html);
	}

	private _computeMaxStars() {
		const activatedBy = this.activatedBy();
		if (TypePredicates.isAssessmentItem(activatedBy)) {
			return activatedBy.starScores.map((_, i) => i);
		}
		if (TypePredicates.isChecklistItem(activatedBy)) {
			return Array(activatedBy.maxStars).map((_, i) => i);
		}

		return [];
	}

	private _computeIsSavingDisabled() {
		// when the item `activatedBy` is chosen, the `selectedValue` should be set to a valid value
		const activatedBy = this.activatedBy();
		const selectedValue = this.selectedValue();
		if (activatedBy) {
			return !isValidValue(selectedValue);
		}
		return false;

		function isValidValue(value: unknown): boolean {
			if (value === null || value === undefined) {
				return false;
			}
			return Array.isArray(value) ? (value as Array<unknown>).length > 0 : true;
		}
	}
}
